import axios from "axios";
import { notifyError, notifySuccess } from "@/components/prospect/prospect.util";
import { notifyWarning } from "@/components/rma/rma.util";

// Viagem do roteiro: sedes e aeroportos, carro x aviao, montagem automatica pelas sugestoes e pontos de apoio do mapa.
// Mixin do ProspectTripEditor: usa o estado dele (trip, form, teams, stops...).
export default {
    methods: {
        // Como chegar e montagem automatica
        async loadTravelData() {
            try {
                const [offices, airports] = await Promise.all([axios.get("/api/admin/prospect/offices"), axios.get("/api/admin/prospect/airports")]);
                this.offices = offices.data.content.offices;
                this.airports = airports.data.content;
            } catch (error) {
                notifyError(this, error);
            }
        },

        // Pontos de apoio (OpenStreetMap) do plano atual; pedidos quando o
        // usuario liga a camada e descartados quando o roteiro muda.
        async loadPois() {
            if (this.poisLoading || !this.trip) return;
            const key = this.poisSignature();
            this.poisLoading = true;
            this.poisError = null;
            try {
                const result = await axios.get(`/api/admin/prospect/trips/${this.id}/pois`);
                if (key !== this.poisSignature()) return;
                this.pois = result.data.content;
                this.poisKey = key;
            } catch (error) {
                this.poisError = "Não foi possível buscar os pontos de apoio.";
            } finally {
                this.poisLoading = false;
            }
        },

        poisSignature() {
            if (!this.trip || !this.trip.plan) return "";
            return (this.trip.plan.teams || []).map((entry) => (entry.plan ? `${entry.team_id}:${entry.plan.case_order.join(",")}:${entry.plan.days.length}:${(entry.plan.geometry || []).length}` : entry.team_id)).join("|");
        },

        // Recalcula carro x aviao quando mudam as cidades ou a sede.
        refreshAccess() {
            if (!this.trip) return;
            const cities = [...new Set(this.trip.stops.map((stop) => (stop.case && stop.case.municipality ? stop.case.municipality.id : null)).filter(Boolean))].sort((a, b) => a - b);
            const key = `${this.trip.office_id || ""}|${cities.join(",")}`;
            if (key === this.accessKey) return;
            this.accessKey = key;
            if (!cities.length) {
                this.access = null;
                return;
            }
            axios.get(`/api/admin/prospect/trips/${this.id}/access`).then((result) => {
                if (this.accessKey === key) this.access = result.data.content;
            }).catch((error) => notifyError(this, error));
        },

        onOfficeChange() {
            this.accessKey = "";
            // De carro, a base acompanha a sede.
            const office = this.offices.find((item) => item.id === this.form.office_id);
            if (this.form.travel_mode === "car" && office && office.municipality) {
                if (!this.baseOptions.some((item) => item.id === office.municipality.id)) this.baseOptions = [office.municipality, ...this.baseOptions];
                this.form.base_id = office.municipality.id;
            }
            this.queueSave();
        },

        setMode(mode) {
            if (!mode || mode === this.form.travel_mode) return;
            if (mode === "flight") {
                const airportId = this.form.arrival_airport_id
                    || (this.access && this.access.best_airport ? this.access.best_airport.id : null)
                    || (this.access && this.access.flights[0] ? this.access.flights[0].airport.id : null);
                if (!airportId) {
                    notifyWarning(this, "Inclua casos para a plataforma sugerir o aeroporto de chegada.");
                    this.modeKey += 1;
                    return;
                }
                this.form.arrival_airport_id = airportId;
            } else {
                const office = this.offices.find((item) => item.id === this.form.office_id);
                if (office && office.municipality) {
                    if (!this.baseOptions.some((item) => item.id === office.municipality.id)) this.baseOptions = [office.municipality, ...this.baseOptions];
                    this.form.base_id = office.municipality.id;
                }
            }
            this.form.travel_mode = mode;
            this.queueSave();
        },

        useFlight(airportId) {
            this.form.arrival_airport_id = airportId;
            this.form.travel_mode = "flight";
            this.queueSave();
        },

        openSuggest() {
            this.suggest = { ...this.suggest, open: true, uf: this.form.uf };
            this.loadSuggest();
        },

        async loadSuggest() {
            this.suggest.loading = true;
            try {
                const params = { trip_id: this.id, max_cases: this.suggest.max_cases };
                if (this.form.office_id) params.office_id = this.form.office_id;
                if (this.suggest.uf) params.uf = this.suggest.uf;
                const result = await axios.get("/api/admin/prospect/trips/suggestions", { params });
                this.suggest.items = result.data.content.suggestions;
            } catch (error) {
                notifyError(this, error);
            } finally {
                this.suggest.loading = false;
            }
        },

        async applySuggestion(item) {
            if (this.stops.length) {
                const confirmed = await this.$dialog.confirm({
                    title: "Usar este grupo",
                    text: `Os ${this.stops.length} casos atuais saem do roteiro e entram os ${item.cases.length} do grupo "${item.title}", na equipe "${this.activeTeam.name}". Confirma?`,
                    actions: { false: "Cancelar", true: { text: "Usar", color: "primary" } },
                });
                if (!confirmed) return;
            }
            const team = this.activeTeam;
            this.form.travel_mode = item.travel_mode;
            this.form.arrival_airport_id = item.arrival_airport ? item.arrival_airport.id : null;
            if (item.travel_mode === "car") {
                if (!this.baseOptions.some((option) => option.id === item.base.id)) this.baseOptions = [item.base, ...this.baseOptions];
                this.form.base_id = item.base.id;
            }
            if (item.ufs.length === 1) this.form.uf = item.ufs[0];
            this.teams.forEach((entry) => {
                entry.locked_days = [];
                entry.manual_order = false;
            });
            this.stops = item.cases.map((entry) => ({ case_id: entry.id, team_key: team.key, position: null, pin_day: null, pin_time: null, locked: false }));
            this.suggest.open = false;
            this.accessKey = "";
            await this.flush();
            this.dirty = true;
            await this.save();
            this.loadCandidates();
            notifySuccess(this, "Roteiro montado com o grupo sugerido.");
        },
    },
};
